import { AssetClassesQuery, PaginateSortDirection, TenantSortBy } from '@/__generated__/graphql';
import { ISelectValue } from '@/components/basicComponents/select';
import { updateWizardSteps } from '@/components/basicComponents/wizard/progressBar';
import { ScrollTo } from '@/components/dealCatalog-page/types';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { ALL_ADVISORY_FIRM_LIST_QUERY } from '@/components/investors-page/queries';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { ISort } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useStatusData } from '@/hooks/useStatusData';
import { MainWrap } from '@/styles/common';
import { useMutation, useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import axios from 'axios';
import { parse } from 'csv-parse/sync';
import fileDownload from 'js-file-download';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { GET_ASSET_CLASSES_QUERY } from '../../assetClassDefaultTargets/queries';
import { UploadDataFile } from '../importAllocations/steps/uploadDataFile';
import Loading from '../modals/loading';
import { IMPORT_LEGAL_ENTITIES_MUTATION } from '../queries';
import { allowFileExtensions } from '../utils';
import { investorEntitiesColumnsSectionData, investorEntitiesDescriptionSectionData, investorEntitiesSteps } from './constants';
import { EntityImportDetail } from './entityImportDetail';
import { GroupImportDetail } from './groupImportDetail';
import { InvestorImportDetail } from './investorImportDetail';
import { GettingStarted } from './steps/gettingStarted';
import { Groups } from './steps/groups';
import { InvestorEntities } from './steps/investorEntities';
import { InvestorHouseholds } from './steps/investorHouseholds';
import {
  IEntityImportComparison,
  IEntityResponse,
  IGroupImportComparison,
  IGroupResponse,
  IHouseholdComparison,
  IHouseholdResponse,
  ILegalEntityImport,
  ImportInvestorEntitiesUpdateMethodOptionEnum,
  TypeWindow
} from './types';

interface ImportInvestorEntitiesProps {
  handleCloseImportInvestorEntities: () => void;
  backToTitle?: string;
}

const fileExtensions = ['CSV'];

const DEFAULT_UPLOAD_ERROR_MESSAGE =
  'Confirm that you are uploading a CSV file, check the required column names, remove any empty or malformed rows, and try again.';

export const ImportInvestorEntities = ({ handleCloseImportInvestorEntities, backToTitle }: ImportInvestorEntitiesProps) => {
  const { user } = useAuth();
  const { data: statusData } = useStatusData();

  const [typeWindow, setTypeWindow] = useState<TypeWindow>('default');
  const [file, setFile] = useState<File | null>(null);
  const [drag, setDrag] = useState(false);
  const [importSteps, setImportSteps] = useState(investorEntitiesSteps);
  const [updateMethod, setUpdateMethod] = useState<ImportInvestorEntitiesUpdateMethodOptionEnum>(null);
  const [selectedAdvisoryFirmId, setSelectedAdvisoryFirmId] = useState('');
  const [advisoryFirmSelectOptions, setAdvisoryFirmSelectOptions] = useState<ISelectValue[]>([]);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'not-supported' });
  const [sort, setSort] = useState<ISort>({ key: '', asc: true });
  const [search, setSearch] = useState('');
  const [primaryFilter, setPrimaryFilter] = useState('');
  const [isValidFile, setIsValidFile] = useState(true);
  const [showTable, setShowTable] = useState(false);
  const [scrollTo, setScrollTo] = useState<ScrollTo>({ y: 0, makeScroll: false });
  const [importData, setImportData] = useState<any>([]);
  const [columnsSectionData, setColumnsSectionData] = useState<typeof investorEntitiesColumnsSectionData>(investorEntitiesColumnsSectionData);

  const [currentEntityRow, setCurrentEntityRow] = useState<IEntityImportComparison | null>(null);
  const [currentInvestorRow, setCurrentInvestorRow] = useState<IHouseholdComparison | null>(null);
  const [currentGroupRow, setCurrentGroupRow] = useState<IGroupImportComparison | null>(null);

  const [entityData, setEntityData] = useState<IEntityResponse | null>(null);
  const [householdData, setHouseholdData] = useState<IHouseholdResponse | null>(null);
  const [groupData, setGroupData] = useState<IGroupResponse | null>(null);

  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);
  const [entityErrors, setEntityErrors] = useState<IEntityImportComparison[]>([]);
  const [householdErrors, setHouseholdErrors] = useState<IHouseholdComparison[]>([]);
  const [groupErrors, setGroupErrors] = useState<IGroupImportComparison[]>([]);

  const [importLegalEntities, { loading: importLegalEntitiesMutationLoading }] = useMutation<{ importLegalEntities: ILegalEntityImport }>(
    IMPORT_LEGAL_ENTITIES_MUTATION,
    {
      onError: () => {
        setFile(null);
        setIsValidFile(false);
      }
    }
  );

  useEffect(() => {
    if (user?.tenant?.type === TenantType.ADVISORY_FIRM) {
      setSelectedAdvisoryFirmId(user?.tenant?.id);
    }
  }, [user]);

  useQuery(ALL_ADVISORY_FIRM_LIST_QUERY, {
    variables: {
      data: {
        sortBy: TenantSortBy.Name,
        sortDirection: PaginateSortDirection.Asc
      }
    },
    onCompleted: ({ allAdvisoryFirmList }) => {
      const selectOptions = [...new Set(allAdvisoryFirmList.map((el) => ({ label: el.name, value: el.id })))];
      setAdvisoryFirmSelectOptions([{ label: 'Platform Manager', value: user?.tenant?.id || '' }, ...selectOptions]);
    }
  });

  const { data: assetClassesData, loading: assetClassesLoading } = useQuery(GET_ASSET_CLASSES_QUERY, {
    onError: (error) => {
      console.error(error);
    }
  });

  const getAssetClassesColumnNames = (assetClass: AssetClassesQuery['assetClasses'], targetSubAssetClasses: boolean) => {
    const hasSubAssetClasses = assetClass.some((assetClass) => assetClass.subAssetClasses.length > 0);

    if (hasSubAssetClasses && targetSubAssetClasses) {
      return assetClass.flatMap((assetClass) => {
        return assetClass.subAssetClasses.map((subAssetClass) => ({
          name: `Asset Class: ${subAssetClass.name}`,
          isRequired: false
        }));
      });
    }

    return assetClass.map((assetClass) => ({
      name: `Asset Class: ${assetClass.name}`,
      isRequired: false
    }));
  };

  useEffect(() => {
    if (assetClassesLoading || !assetClassesData) return;

    const { assetClasses } = assetClassesData || {};
    const sortedAssetClassesColumns = getAssetClassesColumnNames(assetClasses, statusData?.target_sub_asset_classes).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const ocRatioIndex = investorEntitiesColumnsSectionData.columns.findIndex((column) => column.name === 'OC Ratio');
    const updateMethodColumn =
      updateMethod === ImportInvestorEntitiesUpdateMethodOptionEnum.UpdateUsingEntityCRMID
        ? [{ name: 'Entity CRM ID', isRequired: true }]
        : [
            { name: 'Entity Static ID', isRequired: true },
            { name: 'Entity CRM ID', isRequired: false }
          ];

    if (ocRatioIndex !== -1) {
      const newColumns = [
        ...updateMethodColumn,
        ...investorEntitiesColumnsSectionData.columns.slice(0, ocRatioIndex + 1),
        ...sortedAssetClassesColumns,
        ...investorEntitiesColumnsSectionData.columns.slice(ocRatioIndex + 1)
      ];

      setColumnsSectionData({
        ...investorEntitiesColumnsSectionData,
        columns: newColumns
      });
    }
  }, [assetClassesData, assetClassesLoading, updateMethod]);

  const emptyStringToNull = (value: string) => {
    return value === '' ? null : value;
  };

  const getCaseInsensitiveValue = (record: any, columnName: string): any => {
    const key = Object.keys(record).find((k) => k.toLowerCase() === columnName.toLowerCase());
    return key ? emptyStringToNull(record[key]) : undefined;
  };

  const parseFile = async (
    file: File,
    expectedColumns: Array<{ name: string; isRequired: boolean }>
  ): Promise<{ isValid: boolean; records: any[]; errorMessage: string | null }> => {
    // Check file extension
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (fileExt !== 'csv') {
      return {
        isValid: false,
        records: [],
        errorMessage: DEFAULT_UPLOAD_ERROR_MESSAGE
      };
    }

    try {
      const text = await file.text();

      // Parse the header row to check for duplicate columns before parsing the full file
      let headerRow: string[];
      try {
        // Parse just the first line to get column headers
        const firstLineEnd = text.indexOf('\n');
        const headerText = firstLineEnd === -1 ? text : text.substring(0, firstLineEnd);
        headerRow =
          parse(headerText, {
            columns: false,
            skip_empty_lines: false,
            trim: true
          })[0] || [];
      } catch (parseError) {
        return {
          isValid: false,
          records: [],
          errorMessage: DEFAULT_UPLOAD_ERROR_MESSAGE
        };
      }

      // Check for duplicate columns in the header row
      const columnNamesLower = headerRow.map((name) => name.toLowerCase());
      const uniqueColumnNames = new Set(columnNamesLower);

      if (columnNamesLower.length !== uniqueColumnNames.size) {
        // Find duplicate column names
        const duplicates: string[] = [];
        const seen = new Set<string>();

        headerRow.forEach((name) => {
          const lowerName = name.toLowerCase();
          if (seen.has(lowerName)) {
            duplicates.push(name);
          } else {
            seen.add(lowerName);
          }
        });

        return {
          isValid: false,
          records: [],
          errorMessage: `${duplicates.join(', ')} included more than once. Please include each valid column header only once in the import file.`
        };
      }

      // Check for missing required columns
      const requiredColumns = expectedColumns.filter((col) => col.isRequired);
      const headerRowLower = headerRow.map((name) => name.toLowerCase());
      const headerRowSet = new Set(headerRowLower);
      const missingRequiredColumns: string[] = [];

      requiredColumns.forEach((requiredCol) => {
        const lowerRequiredName = requiredCol.name.toLowerCase();
        if (!headerRowSet.has(lowerRequiredName)) {
          missingRequiredColumns.push(requiredCol.name);
        }
      });

      if (missingRequiredColumns.length > 0) {
        return {
          isValid: false,
          records: [],
          errorMessage: `${missingRequiredColumns.join(', ')} is required. Please include all required columns in the import file.`
        };
      }

      // Check for unexpected columns
      const expectedColumnNamesLower = expectedColumns.map((col) => col.name.toLowerCase());
      const expectedColumnSet = new Set(expectedColumnNamesLower);
      const unexpectedColumns: string[] = [];

      headerRow.forEach((columnName) => {
        const lowerColumnName = columnName.toLowerCase();
        if (!expectedColumnSet.has(lowerColumnName)) {
          unexpectedColumns.push(columnName);
        }
      });

      if (unexpectedColumns.length > 0) {
        return {
          isValid: false,
          records: [],
          errorMessage: `Unexpected columns found: ${unexpectedColumns.join(', ')}. Please remove these columns or check the expected column names.`
        };
      }

      // Try to parse the full CSV
      let records: any[];
      try {
        records = parse(text, {
          columns: true,
          skip_empty_lines: true,
          trim: true
        });
      } catch (parseError) {
        return {
          isValid: false,
          records: [],
          errorMessage: DEFAULT_UPLOAD_ERROR_MESSAGE
        };
      }

      return {
        isValid: true,
        records,
        errorMessage: null
      };
    } catch (error) {
      return {
        isValid: false,
        records: [],
        errorMessage: DEFAULT_UPLOAD_ERROR_MESSAGE
      };
    }
  };

  const importFile = async (file: File) => {
    setIsValidFile(true);

    try {
      const { isValid, records: parsedRecords, errorMessage } = await parseFile(file, columnsSectionData.columns);
      if (!isValid) {
        setIsValidFile(false);
        setFile(null);
        setUploadErrorMessage(errorMessage);
        return;
      }

      // Clear any previous error messages on successful parse
      setUploadErrorMessage(null);
      let isValidData = true;

      let recordsToUpdate = parsedRecords.map((record: any): any => {
        const allocationTargets = Object.keys(record)
          .filter((key) => key.toLowerCase().includes('asset class'))
          .map((key) => {
            const assetClass = key.split(/Asset Class:\s*/i)[1];
            return {
              name: assetClass,
              percentage: emptyStringToNull(record[key])
            };
          });

        return {
          id: getCaseInsensitiveValue(record, 'Entity Static ID'),
          entityName: getCaseInsensitiveValue(record, 'Entity Name'),
          entityCRMId: getCaseInsensitiveValue(record, 'Entity CRM ID'),
          householdName: getCaseInsensitiveValue(record, 'Household Name'),
          groupName: getCaseInsensitiveValue(record, 'Group Name'),
          primaryEntity: getCaseInsensitiveValue(record, 'Primary Entity'),
          totalEntityValue: getCaseInsensitiveValue(record, 'Total Entity Value'),
          overCommitRatio: getCaseInsensitiveValue(record, 'OC Ratio'),
          // if no allocation targets are provided (absent field in CSV) then set to undefined
          allocationTargets: allocationTargets?.length ? allocationTargets : undefined,
          aboutInvestorNote: getCaseInsensitiveValue(record, 'About Investor Note'),
          sectorsToAvoid: getCaseInsensitiveValue(record, 'Sectors to Avoid'),
          sri: getCaseInsensitiveValue(record, 'SRI'),
          taxStatus: getCaseInsensitiveValue(record, 'Tax Status'),
          category: getCaseInsensitiveValue(record, 'Entity Category'),
          entityType: getCaseInsensitiveValue(record, 'Entity Type'),
          otherType: getCaseInsensitiveValue(record, 'Entity Type Description'),
          qualifiedPurchaser: getCaseInsensitiveValue(record, 'Qualified Purchaser'),
          qualifiedClient: getCaseInsensitiveValue(record, 'Qualified Client'),
          accreditedInvestor: getCaseInsensitiveValue(record, 'Accredited Investor'),
          custodied: getCaseInsensitiveValue(record, 'Custodied'),
          unmanaged: getCaseInsensitiveValue(record, 'Unmanaged'),
          alwaysShow: getCaseInsensitiveValue(record, 'Always Show'),
          onHold: getCaseInsensitiveValue(record, 'On Hold'),
          entityInception: getCaseInsensitiveValue(record, 'Entity Inception Date'),
          defaultBiteSize1: getCaseInsensitiveValue(record, 'Default Bite Size 1'),
          defaultBiteSize2: getCaseInsensitiveValue(record, 'Default Bite Size 2'),
          advisorEmail: getCaseInsensitiveValue(record, 'Client Advisor Owner'),
          advisorReportingAccountNumber: getCaseInsensitiveValue(record, 'Advisor Reporting Account Number')
        };
      });

      if (!isValidData) return;

      // if no records are provided(all csv row data is empty) set to empty object to produce required field errors
      recordsToUpdate = recordsToUpdate?.length ? recordsToUpdate : [{}];

      setImportData(recordsToUpdate);

      const { data } = await importLegalEntities({
        variables: {
          data: {
            tenantId: selectedAdvisoryFirmId,
            importEntities: recordsToUpdate,
            useEntityCRMId: updateMethod === ImportInvestorEntitiesUpdateMethodOptionEnum.UpdateUsingEntityCRMID,
            save: false
          }
        }
      });

      const { entityResponse, householdResponse, groupResponse } = data?.importLegalEntities ?? {
        entityResponse: null,
        householdResponse: null,
        groupResponse: null
      };

      setEntityData(entityResponse);
      setHouseholdData(householdResponse);
      setGroupData(groupResponse);

      if (entityResponse) {
        const entityErrorRows = entityResponse.entityImportComparisons?.filter((row) => row.status === 'error' || row.status === 'duplicate') ?? [];
        setEntityErrors(entityErrorRows);
      }

      if (householdResponse) {
        const householdErrorRows = householdResponse.householdComparisons?.filter((row) => row.status === 'error') ?? [];
        setHouseholdErrors(householdErrorRows);
      }

      if (groupResponse) {
        const groupErrorRows = groupResponse.groupImportComparisons?.filter((row) => row.status === 'error') ?? [];
        setGroupErrors(groupErrorRows);
      }
    } catch (error) {
      setFile(null);
      setIsValidFile(false);
    }
  };

  const onDropHandler = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;

    setDrag(false);
    if (!allowFileExtensions(uploadFiles, fileExtensions).length) {
      setModalWindow({ isOpen: true, type: 'not-supported' });
      return;
    }
    if (uploadFiles && uploadFiles.length > 0) {
      setFile(uploadFiles[0]);
      importFile(uploadFiles[0]);
    }
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;

    if (!allowFileExtensions(uploadFiles, fileExtensions).length) {
      setModalWindow({ isOpen: true, type: 'not-supported' });
      return;
    }
    if (uploadFiles && uploadFiles.length > 0) {
      setFile(uploadFiles[0]);
      importFile(uploadFiles[0]);
    }
  };

  const resetFilters = (currentStepIndex?: number) => {
    setSort({ key: '', asc: true });
    setSearch('');
    setShowTable(false);

    if (!currentStepIndex) return;
    const nextStep = investorEntitiesSteps[currentStepIndex + 1];
    switch (nextStep.name) {
      case 'Investor Entities':
        if (entityErrors.length) {
          setPrimaryFilter('Errors Only');
          return;
        }
        const isRowsWithUpdateStatusInvestorEntities = entityData?.entityImportComparisons?.some((row) => row.status === 'updated');
        setPrimaryFilter(isRowsWithUpdateStatusInvestorEntities ? 'Updates Only' : 'All Rows');
        break;
      case 'Investor Households':
        if (householdErrors.length) {
          setPrimaryFilter('Errors Only');
          return;
        }
        const isRowsWithUpdateStatusInvestorHouseholds = householdData?.householdComparisons?.some((row) => row.status === 'updated');
        setPrimaryFilter(isRowsWithUpdateStatusInvestorHouseholds ? 'Updates Only' : 'All Rows');
        break;
      case 'Groups':
        setPrimaryFilter(groupErrors.length ? 'Errors Only' : 'Updates Only');
        break;
      default:
        setPrimaryFilter('All Rows');
        break;
    }
  };

  const prevStep = () => {
    const currentStep = importSteps.find((step) => step.status === 'IN_PROGRESS');
    console.log('currentStep', currentStep);
    if (currentStep?.name === 'Investor Entities' || currentStep?.name === 'Upload Data File') {
      setIsValidFile(true);
      setUploadErrorMessage(null);
      setFile(null);
      resetFilters();
    }
    updateWizardSteps('prev', importSteps, setImportSteps);
  };

  const nextStep = () => {
    updateWizardSteps('next', importSteps, setImportSteps);
    const currentStepIndex = importSteps.findIndex((step) => step.status === 'IN_PROGRESS');
    if (currentStepIndex === importSteps.length - 1) return;
    resetFilters(currentStepIndex);
  };

  const handleGoBack = () => {
    if (importSteps[0].status === 'IN_PROGRESS') {
      handleCloseImportInvestorEntities();
      return;
    }
    prevStep();
  };

  const completeImport = async () => {
    if (!importData?.length) return;
    setModalWindow({ isOpen: true, type: 'loading' });

    await importLegalEntities({
      variables: {
        data: {
          tenantId: selectedAdvisoryFirmId,
          importEntities: importData,
          useEntityCRMId: updateMethod === ImportInvestorEntitiesUpdateMethodOptionEnum.UpdateUsingEntityCRMID,
          save: true
        }
      }
    });

    handleCloseImportInvestorEntities();
    setModalWindow({ isOpen: false, type: 'loading' });
  };

  const handleSelectedAdvisoryFirmId = (advisoryFirmId: string) => {
    setSelectedAdvisoryFirmId(advisoryFirmId);
  };

  const handleSelectedUpdateMethod = (updateMethod: ImportInvestorEntitiesUpdateMethodOptionEnum) => {
    setUpdateMethod(updateMethod);
  };

  const onChangeSearch = (value: string) => {
    setSearch(value);
  };

  const changePrimaryFilterValue = (filter: string) => {
    setPrimaryFilter(filter);
  };

  const openEntityImportDetailPage = (type: TypeWindow, row: IEntityImportComparison) => {
    setTypeWindow(type);
    setCurrentEntityRow(row);
    setScrollTo({ y: window.scrollY, makeScroll: true });
  };

  const openInvestorImportDetailPage = (type: TypeWindow, row: IHouseholdComparison) => {
    setTypeWindow(type);
    setCurrentInvestorRow(row);
    setScrollTo({ y: window.scrollY, makeScroll: true });
  };

  const openGroupImportDetailPage = (type: TypeWindow, row: IGroupImportComparison) => {
    setTypeWindow(type);
    setCurrentGroupRow(row);
    setScrollTo({ y: window.scrollY, makeScroll: true });
  };

  const handleCloseDetailPage = () => {
    setTypeWindow('default');
    setCurrentEntityRow(null);
    setCurrentInvestorRow(null);
    setCurrentGroupRow(null);
    if (scrollTo.makeScroll) {
      setTimeout(() => {
        window.scrollTo({ top: scrollTo.y, behavior: 'smooth' });
        setScrollTo({ y: 0, makeScroll: false });
      }, 10);
    }
  };

  const downloadExistingData = async () => {
    if (!selectedAdvisoryFirmId) return;

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/entity/export`,
      {
        tenantId: selectedAdvisoryFirmId,
        entityCRMIdOnly: updateMethod === ImportInvestorEntitiesUpdateMethodOptionEnum.UpdateUsingEntityCRMID
      },
      {
        headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
      }
    );
    fileDownload(response.data, 'investor-entities.csv');
  };

  if (assetClassesLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      {typeWindow === 'entityImportDetail' && (
        <EntityImportDetail currentRow={currentEntityRow} handleClose={handleCloseDetailPage} backToTitle="Import Investor Entities" />
      )}
      {typeWindow === 'investorImportDetail' && (
        <InvestorImportDetail currentRow={currentInvestorRow} handleClose={handleCloseDetailPage} backToTitle="Import Investor Entities" />
      )}
      {typeWindow === 'groupImportDetail' && (
        <GroupImportDetail currentRow={currentGroupRow} handleClose={handleCloseDetailPage} backToTitle="Import Investor Entities" />
      )}
      {typeWindow === 'default' && (
        <>
          {modalWindow.type === 'not-supported' && (
            <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ ...modalWindow, isOpen: false })} />
          )}
          {modalWindow.type === 'loading' && <Loading isOpen={modalWindow.isOpen} />}
          <MainWrap>
            <Header
              modalControl={<GoBackButton handleClose={handleGoBack} backToTitle={importSteps[0].status === 'IN_PROGRESS' ? backToTitle : ''} />}
            />
            <PageTitle title="Import Investor Entities" />
          </MainWrap>
          <MainWrap>
            <PaddingWrap>
              {importSteps[0].status === 'IN_PROGRESS' && (
                <GettingStarted
                  importSteps={importSteps}
                  descriptionSection={investorEntitiesDescriptionSectionData}
                  updateMethod={updateMethod}
                  selectedAdvisoryFirmId={selectedAdvisoryFirmId}
                  advisoryFirmSelectOptions={advisoryFirmSelectOptions}
                  handleSelectedUpdateMethod={handleSelectedUpdateMethod}
                  handleSelectedAdvisoryFirmId={handleSelectedAdvisoryFirmId}
                  nextStep={nextStep}
                  handleClose={handleCloseImportInvestorEntities}
                />
              )}
              {importSteps[1].status === 'IN_PROGRESS' && (
                <UploadDataFile
                  importSteps={importSteps}
                  file={file}
                  handleUploadFile={handleUploadFile}
                  onDropHandler={onDropHandler}
                  drag={drag}
                  setDrag={setDrag}
                  handleClose={prevStep}
                  nextStep={nextStep}
                  loading={importLegalEntitiesMutationLoading}
                  isValidFile={isValidFile}
                  columnsSection={columnsSectionData}
                  buttonLabels={{
                    cancel: 'Back'
                  }}
                  downloadExistingData={downloadExistingData}
                  deleteFile={() => {
                    setFile(null);
                    setUploadErrorMessage(null);
                  }}
                  errorMessage={uploadErrorMessage}
                />
              )}
              {importSteps[2].status === 'IN_PROGRESS' && (
                <InvestorEntities
                  importSteps={importSteps}
                  prevStep={prevStep}
                  nextStep={nextStep}
                  entityData={entityData}
                  updateMethod={updateMethod}
                  errors={entityErrors}
                  sort={sort}
                  setSort={setSort}
                  search={search}
                  onChangeSearch={onChangeSearch}
                  primaryFilter={primaryFilter}
                  showTable={showTable}
                  setShowTable={setShowTable}
                  changePrimaryFilterValue={changePrimaryFilterValue}
                  openEntityImportDetailPage={openEntityImportDetailPage}
                />
              )}
              {importSteps[3].status === 'IN_PROGRESS' && (
                <InvestorHouseholds
                  importSteps={importSteps}
                  prevStep={prevStep}
                  nextStep={nextStep}
                  investorData={householdData}
                  errors={householdErrors}
                  sort={sort}
                  setSort={setSort}
                  search={search}
                  onChangeSearch={onChangeSearch}
                  primaryFilter={primaryFilter}
                  showTable={showTable}
                  setShowTable={setShowTable}
                  changePrimaryFilterValue={changePrimaryFilterValue}
                  openInvestorImportDetailPage={openInvestorImportDetailPage}
                />
              )}
              {importSteps[4].status === 'IN_PROGRESS' && (
                <Groups
                  importSteps={importSteps}
                  prevStep={prevStep}
                  nextStep={completeImport}
                  groupData={groupData}
                  errors={groupErrors}
                  sort={sort}
                  setSort={setSort}
                  search={search}
                  onChangeSearch={onChangeSearch}
                  primaryFilter={primaryFilter}
                  showTable={showTable}
                  setShowTable={setShowTable}
                  changePrimaryFilterValue={changePrimaryFilterValue}
                  openGroupImportDetailPage={openGroupImportDetailPage}
                />
              )}
            </PaddingWrap>
          </MainWrap>
        </>
      )}
    </>
  );
};

const PaddingWrap = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  @media (min-width: 600px) {
    padding-left: 50px;
    padding-right: 50px;
  }
`;
